import { useLayoutEffect, useRef, useState } from 'react'
import { Box, HStack, Icon, Spinner, Text } from '@chakra-ui/react'
import { GoogleLogin } from '@react-oauth/google'
import { FcGoogle } from 'react-icons/fc'
import { getLoggedInUserGroupMembers, googleVerify } from 'API/auth'
import { AUTH_ERROR_CODES } from 'constants/auth-errors'
import { getApiErrorDetails, parseGoogleCredentialEmail } from 'utils/authError'
import { AUTH_MOBILE_ACCENT } from 'components/Layouts/AuthLayout/constants'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import routes from 'routes'
import {
  GOOGLE_SSO_CREDENTIAL_KEY,
  GOOGLE_SSO_INVITATION_TOKEN_KEY,
  OPEN_MY_ACCOUNT_PASSWORD_SETUP_SESSION_KEY
} from 'constants/google-sso'
import { isGoogleNeedsRegistration } from 'types/auth'

const GOOGLE_BTN_HEIGHT_PX = 40

interface Props {
  redirectPath?: string
  /** After successful Google login, open My Account (password section) once the shell loads. */
  openMyAccountForPasswordAfterLogin?: boolean
  /** `primaryFilled` — solid blue CTA on white (e.g. security modal). */
  variant?: 'default' | 'primaryFilled'
  /**
   * If set (e.g. from `?email=` on an invitation link), the user MUST sign in with this
   * exact Google account. Mismatch aborts the flow with a toast — no new user is
   * registered. Case-insensitive compare on the JWT's `email` claim.
   */
  expectedEmail?: string
  /**
   * From `?invitation=` on an invitation link. Carried across the role-select step in
   * sessionStorage so registering with Google also accepts the invitation. Ignored for
   * existing accounts — they keep accepting from the bell.
   */
  invitationToken?: string
}

const GoogleSignInButton = ({
  redirectPath,
  openMyAccountForPasswordAfterLogin,
  variant = 'default',
  expectedEmail,
  invitationToken
}: Props) => {
  const { authStore, groupStore, userStore } = useStores()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [pixelWidth, setPixelWidth] = useState(400)

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const w = el.getBoundingClientRect().width
      if (w > 0) setPixelWidth(Math.max(200, Math.floor(w)))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const handleSuccess = async (credentialResponse: { credential?: string }) => {
    const credential = credentialResponse.credential
    if (!credential) {
      toast.error('Google sign-in failed. Please try again.')
      return
    }
    // Invitation flow guard: if the page knows the expected email (from `?email=`),
    // verify the Google account matches before doing anything else. Blocks creating
    // a new user under the wrong email and avoids confusion when the existing user's
    // Google identity differs from the invited address.
    if (expectedEmail) {
      const googleEmail = parseGoogleCredentialEmail(credential)
      if (googleEmail && googleEmail.toLowerCase() !== expectedEmail.toLowerCase()) {
        toast.error(
          `This invitation is for ${expectedEmail}. Please sign in with that Google account.`
        )
        return
      }
    }
    setLoading(true)
    try {
      const resp = await googleVerify(credential)
      if (isGoogleNeedsRegistration(resp)) {
        sessionStorage.setItem(GOOGLE_SSO_CREDENTIAL_KEY, credential)
        // The role-select step navigates away from the invitation URL, so stash the
        // token for it rather than losing it. Clear any stale one from an earlier try.
        if (invitationToken) sessionStorage.setItem(GOOGLE_SSO_INVITATION_TOKEN_KEY, invitationToken)
        else sessionStorage.removeItem(GOOGLE_SSO_INVITATION_TOKEN_KEY)
        navigate(routes.googleSsoRoleSelect.value)
        return
      }
      if (openMyAccountForPasswordAfterLogin) {
        sessionStorage.setItem(OPEN_MY_ACCOUNT_PASSWORD_SETUP_SESSION_KEY, '1')
      }
      authStore.processLoginResponse(resp)
      try {
        const groupMembers = await getLoggedInUserGroupMembers()
        groupStore.setGroupMembers(groupMembers)
        userStore.setCurrentUserGroupMembers(groupMembers)
        userStore.setCurrentUser(resp.userInfo)
      } catch {
        // Token is valid; group fetch can retry after navigation
      }
      // Post-login routing.
      //  - No memberships → /play-dashboard where the bell surfaces any pending
      //    invitation (existing accounts never auto-accept; user clicks Accept
      //    manually).
      //  - Otherwise → /select-profile, which auto-redirects to /play-dashboard
      //    when the caller has only one profile.
      // Org-switching now happens inside the dashboard via the in-app team
      // filter / org buttons — no more /select-organization split.
      navigate(redirectPath ?? routes.selectProfile.value)
    } catch (error: unknown) {
      const { code, message } = getApiErrorDetails(error)
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 409) {
        if (code === AUTH_ERROR_CODES.GOOGLE_SIGNIN_EMAIL_HAS_PASSWORD) {
          toast.error(
            message ??
              'This email is already registered with email and password. Please sign in with your email and password.'
          )
          const emailHint = parseGoogleCredentialEmail(credential)
          const qs = emailHint ? `?email=${encodeURIComponent(emailHint)}` : ''
          navigate(`${routes.login.value}${qs}`)
          return
        }
      }
      toast.error('Google sign-in failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py="8px">
        <Spinner size="md" color="blue.500" />
      </Box>
    )
  }

  const isPrimaryFilled = variant === 'primaryFilled'
  const borderColor = isPrimaryFilled ? 'blue.500' : AUTH_MOBILE_ACCENT
  const fakeBg = isPrimaryFilled ? 'blue.500' : 'transparent'
  const fakeLabelColor = 'white'
  const ctaLabel = isPrimaryFilled ? 'Sign in with Google' : 'Continue with Google'

  return (
    <Box ref={containerRef} position="relative" w="100%" h="48px" borderRadius="24px">
      <HStack
        pointerEvents="none"
        userSelect="none"
        position="absolute"
        inset={0}
        justify="center"
        spacing={3}
        bg={fakeBg}
        borderRadius="24px"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={borderColor}
        px={4}
        h="48px"
      >
        <Icon as={FcGoogle} boxSize="20px" aria-hidden />
        <Text fontSize="15px" fontWeight="500" color={fakeLabelColor} m={0}>
          {ctaLabel}
        </Text>
      </HStack>
      <Box
        position="absolute"
        left={0}
        right={0}
        top="50%"
        zIndex={1}
        h={`${GOOGLE_BTN_HEIGHT_PX}px`}
        mt={`-${GOOGLE_BTN_HEIGHT_PX / 2}px`}
        opacity={0}
        overflow="hidden"
        cursor="pointer"
        aria-hidden
      >
        <GoogleLogin
          onSuccess={handleSuccess}
          onError={() => toast.error('Google sign-in failed. Please try again.')}
          useOneTap={false}
          type="standard"
          theme={isPrimaryFilled ? 'filled_blue' : 'outline'}
          size="large"
          text={isPrimaryFilled ? 'signin_with' : 'continue_with'}
          shape="rectangular"
          logo_alignment="left"
          width={pixelWidth}
          containerProps={{
            style: {
              width: '100%',
              height: `${GOOGLE_BTN_HEIGHT_PX}px`,
              display: 'flex',
              justifyContent: 'center'
            }
          }}
        />
      </Box>
    </Box>
  )
}

export default observer(GoogleSignInButton)
